import { forwardRef } from "react";
import { cn } from "@/lib/cn";
import { Card } from "@/components/primitives/Card";
import { SectionHeader } from "../SectionHeader";
import type { FieldLabelProps, FormSectionProps, HelperTextProps, InlineErrorProps } from "./FormSection.types";

export const FormSection = forwardRef<HTMLDivElement, FormSectionProps>(
  ({ className, title, description, actions, footer, errorSummary, children, ...props }, ref) => (
    <Card ref={ref} padding="none" className={cn("bfrs-overflow-hidden", className)} {...props}>
      {(title || description || actions) && (
        <div className="bfrs-border-b bfrs-border-border bfrs-px-4 bfrs-py-3">
          <SectionHeader title={title} description={description} actions={actions} compact />
        </div>
      )}
      {errorSummary && <div className="bfrs-border-b bfrs-border-danger/20 bfrs-bg-danger-soft bfrs-px-4 bfrs-py-3 bfrs-text-sm bfrs-text-danger-contrast">{errorSummary}</div>}
      <div className="bfrs-grid bfrs-gap-4 bfrs-px-4 bfrs-py-4">{children}</div>
      {footer && <div className="bfrs-border-t bfrs-border-border bfrs-bg-surface-muted/60 bfrs-px-4 bfrs-py-3">{footer}</div>}
    </Card>
  )
);

FormSection.displayName = "FormSection";

export const FieldLabel = forwardRef<HTMLLabelElement, FieldLabelProps>(({ className, required, children, ...props }, ref) => (
  <label ref={ref} className={cn("bfrs-block bfrs-text-[12px] bfrs-font-semibold bfrs-text-text-primary", className)} {...props}>
    {children}
    {required && <span className="bfrs-ml-1 bfrs-text-danger">*</span>}
  </label>
));

FieldLabel.displayName = "FieldLabel";

export const HelperText = forwardRef<HTMLParagraphElement, HelperTextProps>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn("bfrs-text-xs bfrs-leading-5 bfrs-text-text-muted", className)} {...props} />
));

HelperText.displayName = "HelperText";

export const InlineError = forwardRef<HTMLParagraphElement, InlineErrorProps>(({ className, ...props }, ref) => (
  <p ref={ref} className={cn("bfrs-text-xs bfrs-leading-5 bfrs-text-danger", className)} {...props} />
));

InlineError.displayName = "InlineError";
